import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface ListingSkeletonProps {
  view: 'grid' | 'table' | 'compact';
}

function ListingSkeleton({ view }: ListingSkeletonProps) {
  if (view === 'compact') {
    return (
      <div className='space-y-2'>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className='flex items-center gap-3 rounded-md border px-4 py-2.5'
          >
            <Skeleton className='h-8 w-8 shrink-0 rounded-full' />
            <div className='min-w-0 flex-1 space-y-1.5'>
              <Skeleton className='h-4 w-1/3' />
              <Skeleton className='h-3 w-1/5' />
            </div>
            <div className='hidden items-center gap-2 sm:flex'>
              <Skeleton className='h-5 w-16 rounded-full' />
              <Skeleton className='h-5 w-14 rounded-full' />
            </div>
            <div className='flex items-center gap-1'>
              <Skeleton className='h-7 w-7 rounded-md' />
              <Skeleton className='h-7 w-7 rounded-md' />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (view === 'table') {
    return (
      <div className='space-y-3'>
        <div className='rounded-md border'>
          {/* Header row */}
          <div className='border-b p-4'>
            <div className='flex gap-4'>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className='h-5 flex-1' />
              ))}
            </div>
          </div>
          {/* Data rows */}
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={cn('flex gap-4 p-4', i < 5 && 'border-b')}>
              {Array.from({ length: 5 }).map((_, j) => (
                <Skeleton key={j} className='h-5 flex-1' />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className='rounded-lg border'>
          <div className='space-y-4 p-6'>
            {/* Top: avatar + name + badge */}
            <div className='flex items-start gap-3'>
              <Skeleton className='h-10 w-10 shrink-0 rounded-full' />
              <div className='min-w-0 flex-1 space-y-1.5'>
                <Skeleton className='h-4 w-2/3' />
                <Skeleton className='h-3 w-1/3' />
              </div>
              <Skeleton className='h-5 w-14 shrink-0 rounded-full' />
            </div>
            {/* Middle: text lines */}
            <div className='space-y-2'>
              <Skeleton className='h-3 w-full' />
              <Skeleton className='h-3 w-4/5' />
            </div>
          </div>
          {/* Footer */}
          <div className='flex items-center justify-end gap-2 border-t px-6 py-3'>
            <Skeleton className='h-8 w-8 rounded-md' />
            <Skeleton className='h-8 w-8 rounded-md' />
          </div>
        </div>
      ))}
    </div>
  );
}

export { ListingSkeleton };
